import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { testimonials } from "@/mocks/entries";
import { siteConfig, stats } from "@/mocks/site";

const principles = [
  {
    icon: "ri-quill-pen-line",
    title: "只写可核验的内容",
    text: "每个节点都尽量对应可查证的公开来源。无法确认的部分，我们会标注为「仍存疑」，而不是含糊带过。",
  },
  {
    icon: "ri-scales-3-line",
    title: "并列而非裁定",
    text: "各方回应并列呈现，不用叙述顺序暗示立场。词条的任务是整理信息，而不是替读者下结论。",
  },
  {
    icon: "ri-shield-check-line",
    title: "划清讨论边界",
    text: "我们讨论事件本身与规则问题，明确拒绝针对个人的身份挖掘、外貌评价与人身攻击。",
  },
  {
    icon: "ri-refresh-line",
    title: "修订留痕",
    text: "词条更新时同步标注修改时间与内容范围，读者可以清楚看到一条记录是如何演化的。",
  },
];

const milestones = [
  { year: "2024", title: "从一份表格开始", text: "最初只是编辑团队内部用来跟进热点的时间线文档，后来被整理成公开词条。" },
  { year: "2025", title: "建立统一词条结构", text: "确立背景、经过、完整记录、回应、理性看待五段式，所有词条按同一模板沉淀。" },
  { year: "2026", title: "开放更正与补充", text: "上线更正提交通道，读者可以附来源指出词条疏漏，修订记录对外可见。" },
];

export default function About() {
  const jsonLd = useMemo(() => {
    const origin = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 黑料百科",
        description: siteConfig.description,
        inLanguage: "zh-CN",
        url: `${origin}/about`,
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteConfig.name,
        description: siteConfig.description,
        email: siteConfig.email,
        url: origin,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${origin}/about` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: "关于我们 - 黑料百科的定位、内容原则与联系方式",
    description:
      "了解黑料百科的定位与内容原则：以结构化词条整理网络事件，区分事实与各方说法，倡导理性讨论、不信谣不传谣，并开放公开可查证的更正通道。",
    keywords: "关于黑料百科,内容原则,网站定位,事件词条,理性讨论",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 py-8 md:px-10 md:py-12">
          <Breadcrumbs items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <span className="font-heading text-sm font-bold tracking-[0.2em] text-primary-600">
                ABOUT US
              </span>
              <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
                一个把热点整理成词条的地方
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-600 md:text-lg">
                {siteConfig.name}是一个以事件为单位的信息库。我们相信，一个瓜条真正的价值不在于它多刺激，
                而在于它是否能被清楚地讲述——起因是什么、经过了哪些节点、当事各方说了什么、哪些部分还没有答案。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {stats.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="font-heading text-2xl font-black text-primary-600">{item.value}</div>
                    <div className="mt-1 text-xs text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-50 px-4 py-14 md:px-10 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">网站定位</h2>
              <p className="mt-4 text-base leading-8 text-foreground-700">
                我们不做新闻，也不做评论。网站的核心是一套稳定的词条结构，让后来者可以快速补上事件的来龙去脉。
              </p>
              <p className="mt-4 text-base leading-8 text-foreground-700">
                对读者来说，它像一本可以随时翻查的索引；对内容从业者来说，它是一份结构清晰的起点资料。
                无论哪种身份，你都不需要先看十篇不同立场的文章，才能拼出事情的全貌。
              </p>
            </div>
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">发展理念</h2>
              <ol className="mt-6 space-y-5">
                {milestones.map((m, index) => (
                  <li key={m.year} className="flex gap-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-500 font-heading text-sm font-bold text-secondary-950">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950">
                        {m.year} · {m.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{m.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-100 px-4 py-14 md:px-10 md:py-20">
          <div className="max-w-3xl">
            <span className="font-heading text-sm font-bold tracking-[0.2em] text-secondary-600">
              EDITORIAL PRINCIPLES
            </span>
            <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              四条内容原则
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              这四条原则写进了每一条词条的编辑流程，也是我们接受更正与监督的依据。
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <article
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-secondary-50">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-50 px-4 py-14 md:px-10 md:py-20">
          <div className="mb-10 max-w-3xl">
            <h2 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              用户口碑
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              来自内容运营、学生与媒体编辑的真实使用反馈。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
              >
                <blockquote className="text-sm leading-relaxed text-foreground-700">
                  <i className="ri-double-quotes-l mr-1 text-lg text-primary-300" />
                  {item.text}
                </blockquote>
                <figcaption className="mt-5 border-t border-background-200 pt-4 text-sm">
                  <span className="block font-medium text-foreground-900">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full bg-secondary-900 px-4 py-14 md:px-10 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-black text-secondary-50 md:text-3xl">联系方式</h2>
              <p className="mt-3 text-sm leading-relaxed text-secondary-100/80">
                内容更正、线索补充与合作咨询，都可以通过以下方式联系我们，通常两个工作日内回复。
              </p>
            </div>
            <div className="flex flex-col gap-3 lg:col-span-5">
              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-center gap-3 rounded-lg border border-secondary-700/60 bg-secondary-800/40 px-5 py-4 text-sm text-secondary-50 transition-colors hover:border-accent-300"
              >
                <i className="ri-mail-line text-lg text-accent-300" />
                <span className="break-all">{siteConfig.email}</span>
              </a>
              <Link
                to="/contact"
                className="flex items-center gap-3 rounded-lg bg-accent-500 px-5 py-4 text-sm font-medium text-secondary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-chat-3-line text-lg" />
                前往联系页面提交说明
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}